import { AlertTriangle, CheckCircle2 } from "lucide-react"

import type { ReportDesignerValidationIssue } from "./reportDesignerValidation"
import { Badge } from "shared/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"

interface ValidationSummaryCardProps {
  title: string
  issues: ReportDesignerValidationIssue[]
  category?: ReportDesignerValidationIssue["category"] | "all"
  emptyMessage: string
}

export function ValidationSummaryCard({
  title,
  issues,
  category = "all",
  emptyMessage,
}: ValidationSummaryCardProps) {
  const scopedIssues = category === "all" ? issues : issues.filter((issue) => issue.category === category)
  const errors = scopedIssues.filter((issue) => issue.severity === "error")
  const warnings = scopedIssues.filter((issue) => issue.severity === "warning")

  if (scopedIssues.length === 0) {
    return (
      <Card className="border-emerald-200 bg-emerald-50/60">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-emerald-800">
            <CheckCircle2 className="h-4 w-4" />
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-emerald-900">{emptyMessage}</CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader className="space-y-2 pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-amber-900">
          <AlertTriangle className="h-4 w-4" />
          {title}
        </CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          {errors.length > 0 ? <Badge variant="destructive">{errors.length} error(s)</Badge> : null}
          {warnings.length > 0 ? <Badge variant="secondary">{warnings.length} warning(s)</Badge> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {scopedIssues.map((issue) => (
          <div
            key={issue.id}
            className={`rounded-md border px-3 py-2 text-sm ${
              issue.severity === "error"
                ? "border-rose-200 bg-rose-50 text-rose-900"
                : "border-amber-200 bg-white/70 text-amber-900"
            }`}
          >
            <div className="flex items-start gap-2">
              <Badge variant={issue.severity === "error" ? "destructive" : "secondary"} className="mt-0.5">
                {issue.severity}
              </Badge>
              <span>{issue.message}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
